import { useEffect, useMemo, useRef, useState } from 'react'

import styled from 'styled-components'

import { runInspectionToolPreview } from '../../../../api/inspectionTools'
import { useNotifications } from '../../../../contexts/notificationProvider'
import { AddProjectButton } from '../../buildings_page/components/AddProjectButton'

interface ExcludableLabelOption {
  id: number
  name: string
  supercategory: 'inspection' | 'hydraulic_leak'
}

const EXCLUDABLE_LABEL_OPTIONS: ExcludableLabelOption[] = [
  { id: 0, name: '10_torque_mark', supercategory: 'inspection' },
  { id: 1, name: '11_aligned_torque_mark', supercategory: 'inspection' },
  { id: 2, name: '12_misaligned_torque_mark', supercategory: 'inspection' },
  { id: 3, name: '13_incomplete_torque_mark', supercategory: 'inspection' },
  { id: 4, name: '20_missing_torque_mark', supercategory: 'inspection' },
  {
    id: 5,
    name: '30_missing_torque_mark_partially_obscured',
    supercategory: 'inspection',
  },
  {
    id: 6,
    name: 'd13_danger_electrocution_hazard',
    supercategory: 'inspection',
  },
  {
    id: 7,
    name: 'd12_warning_crushing_hazard_front',
    supercategory: 'inspection',
  },
  {
    id: 8,
    name: 'd16_warning_crushing_hazard_back',
    supercategory: 'inspection',
  },
  { id: 9, name: 'dx1_hazardous_voltage', supercategory: 'inspection' },
  { id: 10, name: 'leak_small', supercategory: 'inspection' },
  { id: 11, name: 'leak_large', supercategory: 'inspection' },
  { id: 12, name: 'd11_danger_warning', supercategory: 'inspection' },
  { id: 13, name: 'd29_warning_dual_cap', supercategory: 'inspection' },
  { id: 14, name: 'd38_reflective_tape', supercategory: 'inspection' },
  { id: 15, name: 'd65_radio_frequency', supercategory: 'inspection' },
  { id: 16, name: 'd40_lift_diagram', supercategory: 'inspection' },
  { id: 17, name: 'dx2_dual_capacity', supercategory: 'inspection' },
  { id: 18, name: 'dx3_inspection', supercategory: 'inspection' },
  { id: 19, name: 'd1415_hook_front', supercategory: 'inspection' },
  { id: 100, name: 'Hydraulic Leak', supercategory: 'hydraulic_leak' },
  { id: 101, name: 'leak_risk_area', supercategory: 'hydraulic_leak' },
  { id: 102, name: 'rgb_leak_class2_line', supercategory: 'hydraulic_leak' },
  { id: 103, name: 'rgb_leak_class3_droplet', supercategory: 'hydraulic_leak' },
  { id: 104, name: 'rgb_leak_class4_trail', supercategory: 'hydraulic_leak' },
  { id: 105, name: 'rgb_leak_class5_puddle', supercategory: 'hydraulic_leak' },
  { id: 106, name: 'xray_leak_class2_line', supercategory: 'hydraulic_leak' },
  { id: 107, name: 'xray_class3_droplet', supercategory: 'hydraulic_leak' },
  { id: 108, name: 'xray_class4_trail', supercategory: 'hydraulic_leak' },
  { id: 109, name: 'xray_class5_puddle', supercategory: 'hydraulic_leak' },
]

const DEFAULT_EXCLUDED_LABELS = ['leak_risk_area']

const Section = styled.div`
  background: #ffffff;
  border: 1px solid #e6e8ec;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
`

const SectionTitle = styled.h2`
  margin: 0 0 12px;
`

const SectionSubtitle = styled.p`
  margin: 0 0 20px;
  color: #666;
`

const Label = styled.label`
  display: block;
  font-size: 12px;
  color: #4a4a4a;
  margin-bottom: 8px;
`

const TextInput = styled.input`
  width: 100%;
  border: 1px solid #d5d7dc;
  border-radius: 6px;
  padding: 10px;
  font-size: 14px;
  box-sizing: border-box;
`

const FileInput = styled.input`
  display: none;
`

const ResponseCode = styled.pre`
  background: #141821;
  color: #e6ecff;
  padding: 12px;
  border-radius: 8px;
  max-height: 420px;
  overflow: auto;
  font-size: 12px;
`

const PreviewImage = styled.img<{ $scale: number }>`
  width: ${props => `${props.$scale * 100}%`};
  max-width: none;
  border: 1px solid #d5d7dc;
  border-radius: 6px;
  margin-top: 0;
  user-select: none;
  -webkit-user-drag: none;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
`

const UploadDropZone = styled.button<{ isDragging: boolean; hasFile: boolean }>`
  width: 100%;
  min-height: 124px;
  border: 2px dashed
    ${props =>
      props.hasFile ? '#0f7a43' : props.isDragging ? '#073c7a' : '#c9d2de'};
  background: ${props =>
    props.hasFile ? '#ecfff3' : props.isDragging ? '#f1f7ff' : '#f8fafc'};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  color: #3e4a59;
`

const UploadDropZoneText = styled.div`
  font-size: 14px;
  line-height: 20px;
`

const UploadReadyLabel = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #0f7a43;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 10px;
  margin-bottom: 10px;
`

const UploadFileName = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  border: 1px solid #9fd8b8;
  background: #ffffff;
  border-radius: 6px;
  font-weight: 600;
  color: #1a3b2a;
`

const FieldBlock = styled.div`
  margin-top: 16px;
`

const DropdownContainer = styled.div`
  position: relative;
`

const DropdownTrigger = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid #d5d7dc;
  border-radius: 6px;
  background: #fff;
  padding: 10px;
  font-size: 14px;
  cursor: pointer;
`

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 6;
  border: 1px solid #d5d7dc;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  max-height: 320px;
  overflow: auto;
`

const DropdownActions = styled.div`
  position: sticky;
  top: 0;
  background: #fff;
  border-bottom: 1px solid #eceff3;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const DropdownActionButton = styled.button`
  border: none;
  background: none;
  color: #0a4f95;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
`

const DropdownOptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  font-size: 13px;
  border-bottom: 1px solid #f3f5f8;
`

const DropdownOptionMeta = styled.span`
  margin-left: auto;
  font-size: 11px;
  color: #6c7787;
  text-transform: uppercase;
`

const ActionsRow = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`

const ActionButtonWrapper = styled.div<{ disabled?: boolean }>`
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`

const ImageViewerToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`

const ZoomButton = styled.button`
  border: 1px solid #c9d2de;
  background: #fff;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
`

const ZoomRange = styled.input`
  width: 180px;
`

const ImageViewport = styled.div`
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  padding: 10px;
  overflow: hidden;
  max-height: 70vh;
  background: #fafbfc;
  position: relative;
  touch-action: none;
`

const TransformLayer = styled.div<{
  $offsetX: number
  $offsetY: number
  $isDragging: boolean
}>`
  transform: translate(
    ${props => props.$offsetX}px,
    ${props => props.$offsetY}px
  );
  transform-origin: top left;
  will-change: transform;
  cursor: ${props => (props.$isDragging ? 'grabbing' : 'grab')};
`

const ZoomHint = styled.div`
  position: absolute;
  right: 12px;
  top: 12px;
  z-index: 3;
  font-size: 12px;
  background: rgba(20, 24, 33, 0.84);
  color: #fff;
  padding: 4px 8px;
  border-radius: 999px;
`

export const MLEndpointTool = () => {
  const DEFAULT_TRANSFORM = { scale: 1, offsetX: 0, offsetY: 0 }
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [confidenceInput, setConfidenceInput] = useState<string>('')
  const [excludedLabels, setExcludedLabels] = useState<string[]>(
    DEFAULT_EXCLUDED_LABELS,
  )
  const [isExcludeDropdownOpen, setIsExcludeDropdownOpen] = useState(false)
  const [annotatedImageBase64, setAnnotatedImageBase64] = useState<string>('')
  const [transform, setTransform] = useState(DEFAULT_TRANSFORM)
  const [isImageDragging, setIsImageDragging] = useState(false)
  const [isImageFullscreen, setIsImageFullscreen] = useState(false)
  const [responsePayload, setResponsePayload] = useState<
    Record<string, unknown>
  >({})
  const [isLoading, setIsLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const excludeDropdownRef = useRef<HTMLDivElement | null>(null)
  const imageViewportRef = useRef<HTMLDivElement | null>(null)
  const dragStateRef = useRef<{
    startX: number
    startY: number
    originOffsetX: number
    originOffsetY: number
  } | null>(null)
  const pinchStateRef = useRef<{
    startDistance: number
    startScale: number
    startOffsetX: number
    startOffsetY: number
    startMidX: number
    startMidY: number
  } | null>(null)

  const { addNotification } = useNotifications()

  const confidenceValue = useMemo(() => {
    if (confidenceInput.trim() === '') {
      return null
    }
    const parsed = Number(confidenceInput)
    if (Number.isNaN(parsed)) {
      return null
    }
    return parsed
  }, [confidenceInput])

  const excludedLabelDisplay = useMemo(() => {
    if (!excludedLabels.length) return 'No excluded labels selected'
    if (excludedLabels.length === 1) return excludedLabels[0]
    return `${excludedLabels.length} labels selected`
  }, [excludedLabels])

  const onProcessImage = async () => {
    if (!selectedFile) {
      addNotification('Please choose an image first', 'warning')
      return
    }

    if (
      confidenceValue !== null &&
      (confidenceValue < 0 ||
        confidenceValue > 1 ||
        Number.isNaN(confidenceValue))
    ) {
      addNotification('Confidence must be between 0.0 and 1.0', 'warning')
      return
    }

    setIsLoading(true)
    try {
      const response = await runInspectionToolPreview({
        imageFile: selectedFile,
        confidence: confidenceValue,
        excludedLabels,
      })
      setAnnotatedImageBase64(response.annotated_image_base64)
      setTransform(DEFAULT_TRANSFORM)
      setResponsePayload(response.ml_response)
      addNotification('Image processed successfully', 'success')
    } catch (error) {
      addNotification('Image processing failed', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const onChooseFile = () => {
    fileInputRef.current?.click()
  }

  const onSetImageFile = (file: File | null) => {
    setSelectedFile(file)
  }

  const onDownloadProcessedImage = () => {
    if (!imageHref) return
    const anchor = document.createElement('a')
    anchor.href = imageHref
    anchor.download = 'inspection-tools-annotated.png'
    anchor.click()
  }

  const imageHref = annotatedImageBase64
    ? `data:image/png;base64,${annotatedImageBase64}`
    : ''

  const clamp = (value: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, value))
  }

  const onResetTransform = () => {
    setTransform(DEFAULT_TRANSFORM)
  }

  const onToggleFullscreen = async () => {
    const viewport = imageViewportRef.current
    if (!viewport) return

    try {
      if (document.fullscreenElement === viewport) {
        await document.exitFullscreen()
        return
      }

      if (!document.fullscreenElement) {
        await viewport.requestFullscreen()
      }
    } catch (error) {
      addNotification('Unable to toggle fullscreen mode', 'warning')
    }
  }

  const updateZoom = (nextScale: number) => {
    setTransform(previous => ({
      ...previous,
      scale: clamp(nextScale, 1, 6),
    }))
  }

  const onWheelImage = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault()
    const container = imageViewportRef.current
    if (!container) return

    const zoomFactor = event.deltaY < 0 ? 1.06 : 0.94
    const nextScale = clamp(transform.scale * zoomFactor, 1, 6)
    if (nextScale === transform.scale) return

    const rect = container.getBoundingClientRect()
    const focalX = event.clientX - rect.left
    const focalY = event.clientY - rect.top
    const imageX = (focalX - transform.offsetX) / transform.scale
    const imageY = (focalY - transform.offsetY) / transform.scale

    setTransform({
      scale: nextScale,
      offsetX: focalX - imageX * nextScale,
      offsetY: focalY - imageY * nextScale,
    })
  }

  const onMouseDownImage = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originOffsetX: transform.offsetX,
      originOffsetY: transform.offsetY,
    }
    setIsImageDragging(true)
  }

  const onTouchStartImage = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length >= 2) {
      const container = imageViewportRef.current
      if (!container) return
      const [touchA, touchB] = [event.touches[0], event.touches[1]]
      const distance = Math.hypot(
        touchB.clientX - touchA.clientX,
        touchB.clientY - touchA.clientY,
      )
      const rect = container.getBoundingClientRect()
      const midpointX = (touchA.clientX + touchB.clientX) / 2 - rect.left
      const midpointY = (touchA.clientY + touchB.clientY) / 2 - rect.top
      pinchStateRef.current = {
        startDistance: distance,
        startScale: transform.scale,
        startOffsetX: transform.offsetX,
        startOffsetY: transform.offsetY,
        startMidX: midpointX,
        startMidY: midpointY,
      }
      dragStateRef.current = null
      setIsImageDragging(false)
      return
    }

    if (event.touches.length === 1) {
      const touch = event.touches[0]
      dragStateRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        originOffsetX: transform.offsetX,
        originOffsetY: transform.offsetY,
      }
      setIsImageDragging(true)
    }
  }

  const onTouchMoveImage = (event: React.TouchEvent<HTMLDivElement>) => {
    const container = imageViewportRef.current
    if (!container) return

    if (event.touches.length >= 2 && pinchStateRef.current) {
      event.preventDefault()
      const [touchA, touchB] = [event.touches[0], event.touches[1]]
      const currentDistance = Math.hypot(
        touchB.clientX - touchA.clientX,
        touchB.clientY - touchA.clientY,
      )
      const pinchState = pinchStateRef.current
      const pinchRatio = currentDistance / pinchState.startDistance
      const dampedRatio = Math.pow(pinchRatio, 0.7)
      const nextScale = clamp(pinchState.startScale * dampedRatio, 1, 6)

      const rect = container.getBoundingClientRect()
      const midpointX = (touchA.clientX + touchB.clientX) / 2
      const midpointY = (touchA.clientY + touchB.clientY) / 2
      const relativeX = midpointX - rect.left
      const relativeY = midpointY - rect.top
      const imageX =
        (pinchState.startMidX - pinchState.startOffsetX) / pinchState.startScale
      const imageY =
        (pinchState.startMidY - pinchState.startOffsetY) / pinchState.startScale

      setTransform({
        scale: nextScale,
        offsetX: relativeX - imageX * nextScale,
        offsetY: relativeY - imageY * nextScale,
      })
      return
    }

    if (event.touches.length === 1 && dragStateRef.current) {
      event.preventDefault()
      const touch = event.touches[0]
      const dragState = dragStateRef.current
      const dx = touch.clientX - dragState.startX
      const dy = touch.clientY - dragState.startY
      setTransform(previous => ({
        ...previous,
        offsetX: dragState.originOffsetX + dx,
        offsetY: dragState.originOffsetY + dy,
      }))
    }
  }

  const onTouchEndImage = () => {
    if (pinchStateRef.current) {
      pinchStateRef.current = null
    }
    dragStateRef.current = null
    setIsImageDragging(false)
  }

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      const dropdownElement = excludeDropdownRef.current
      if (!dropdownElement) return
      if (dropdownElement.contains(event.target as Node)) return
      setIsExcludeDropdownOpen(false)
    }

    document.addEventListener('mousedown', onDocumentClick)
    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
    }
  }, [])

  useEffect(() => {
    if (!isImageDragging) return

    const onMove = (event: MouseEvent) => {
      const dragState = dragStateRef.current
      if (!dragState) return
      const dx = event.clientX - dragState.startX
      const dy = event.clientY - dragState.startY
      setTransform(previous => ({
        ...previous,
        offsetX: dragState.originOffsetX + dx,
        offsetY: dragState.originOffsetY + dy,
      }))
    }

    const onUp = () => {
      dragStateRef.current = null
      setIsImageDragging(false)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [isImageDragging])

  useEffect(() => {
    const onFullscreenChange = () => {
      const viewport = imageViewportRef.current
      setIsImageFullscreen(
        Boolean(viewport && document.fullscreenElement === viewport),
      )
    }

    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange)
    }
  }, [])

  return (
    <>
      <Section>
        <SectionTitle>ML Endpoint Test</SectionTitle>
        <SectionSubtitle>
          Upload an image, optionally set a confidence threshold, and hide
          labels you do not want rendered.
        </SectionSubtitle>

        <Label htmlFor="inspection-tool-upload">Upload Image</Label>
        <UploadDropZone
          type="button"
          isDragging={isDragOver}
          hasFile={Boolean(selectedFile)}
          onClick={onChooseFile}
          onDragOver={event => {
            event.preventDefault()
            setIsDragOver(true)
          }}
          onDragLeave={event => {
            event.preventDefault()
            setIsDragOver(false)
          }}
          onDrop={event => {
            event.preventDefault()
            setIsDragOver(false)
            const droppedFile = event.dataTransfer?.files?.[0] ?? null
            onSetImageFile(droppedFile)
          }}
        >
          <UploadDropZoneText>
            {selectedFile ? (
              <>
                <UploadReadyLabel>FILE ADDED</UploadReadyLabel>
                <div>Image ready to process</div>
                <UploadFileName>{selectedFile.name}</UploadFileName>
                <div style={{ marginTop: 10, color: '#2d5a44' }}>
                  Click or drag another image to replace
                </div>
              </>
            ) : (
              <>
                Drag and drop an image here, or click to upload
                <br />
                PNG / JPG supported
              </>
            )}
          </UploadDropZoneText>
        </UploadDropZone>
        <FileInput
          ref={fileInputRef}
          id="inspection-tool-upload"
          type="file"
          accept="image/*"
          onChange={event => onSetImageFile(event.target.files?.[0] ?? null)}
        />

        <FieldBlock>
          <Label htmlFor="inspection-tool-confidence">
            Confidence Threshold (0.0 - 1.0, optional)
          </Label>
          <TextInput
            id="inspection-tool-confidence"
            type="number"
            min="0"
            max="1"
            step="0.01"
            placeholder="No threshold"
            value={confidenceInput}
            onChange={event => setConfidenceInput(event.target.value)}
          />
        </FieldBlock>

        <FieldBlock>
          <Label htmlFor="inspection-tool-excluded">Excluded Labels</Label>
          <DropdownContainer ref={excludeDropdownRef}>
            <DropdownTrigger
              type="button"
              id="inspection-tool-excluded"
              onClick={() => setIsExcludeDropdownOpen(previous => !previous)}
            >
              {excludedLabelDisplay}
            </DropdownTrigger>
            {isExcludeDropdownOpen ? (
              <DropdownMenu>
                <DropdownActions>
                  <DropdownActionButton
                    type="button"
                    onClick={() =>
                      setExcludedLabels(
                        EXCLUDABLE_LABEL_OPTIONS.map(option => option.name),
                      )
                    }
                  >
                    Select All
                  </DropdownActionButton>
                  <DropdownActionButton
                    type="button"
                    onClick={() => setExcludedLabels(DEFAULT_EXCLUDED_LABELS)}
                  >
                    Reset Default
                  </DropdownActionButton>
                </DropdownActions>
                {EXCLUDABLE_LABEL_OPTIONS.map(option => (
                  <DropdownOptionRow key={option.id}>
                    <input
                      type="checkbox"
                      checked={excludedLabels.includes(option.name)}
                      onChange={event => {
                        const checked = event.target.checked
                        setExcludedLabels(previous =>
                          checked
                            ? [...previous, option.name]
                            : previous.filter(label => label !== option.name),
                        )
                      }}
                    />
                    <span>{option.name}</span>
                    <DropdownOptionMeta>
                      {option.supercategory}
                    </DropdownOptionMeta>
                  </DropdownOptionRow>
                ))}
              </DropdownMenu>
            ) : null}
          </DropdownContainer>
        </FieldBlock>

        <ActionsRow>
          <ActionButtonWrapper disabled={isLoading}>
            <AddProjectButton
              text={isLoading ? 'Processing...' : 'Process Image'}
              onClick={() => onProcessImage()}
            />
          </ActionButtonWrapper>
          {annotatedImageBase64 ? (
            <AddProjectButton
              text="Download Image"
              onClick={() => onDownloadProcessedImage()}
            />
          ) : null}
        </ActionsRow>
      </Section>

      {annotatedImageBase64 ? (
        <Section>
          <SectionTitle as="h3">Annotated Image</SectionTitle>
          <ImageViewerToolbar>
            <ZoomButton
              type="button"
              onClick={() => updateZoom(transform.scale - 0.1)}
            >
              -
            </ZoomButton>
            <ZoomButton
              type="button"
              onClick={() => updateZoom(transform.scale + 0.1)}
            >
              +
            </ZoomButton>
            <ZoomButton type="button" onClick={onResetTransform}>
              Reset
            </ZoomButton>
            <ZoomButton type="button" onClick={onToggleFullscreen}>
              {isImageFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            </ZoomButton>
            <ZoomRange
              type="range"
              min="1"
              max="6"
              step="0.1"
              value={transform.scale}
              onChange={event => updateZoom(Number(event.target.value))}
            />
            <div>{Math.round(transform.scale * 100)}%</div>
          </ImageViewerToolbar>
          <ImageViewport
            ref={imageViewportRef}
            onWheel={onWheelImage}
            onMouseDown={onMouseDownImage}
            onDoubleClick={onResetTransform}
            onTouchStart={onTouchStartImage}
            onTouchMove={onTouchMoveImage}
            onTouchEnd={onTouchEndImage}
          >
            <ZoomHint>
              {Math.round(transform.scale * 100)}% · wheel/pinch to zoom · drag
              to pan · dbl-click reset
            </ZoomHint>
            <TransformLayer
              $offsetX={transform.offsetX}
              $offsetY={transform.offsetY}
              $isDragging={isImageDragging}
            >
              <PreviewImage
                $scale={transform.scale}
                src={imageHref}
                alt="Annotated inspection result"
              />
            </TransformLayer>
          </ImageViewport>
        </Section>
      ) : null}

      {Object.keys(responsePayload).length ? (
        <Section>
          <SectionTitle as="h3">Detection JSON</SectionTitle>
          <ResponseCode>
            {JSON.stringify(responsePayload, null, 2)}
          </ResponseCode>
        </Section>
      ) : null}
    </>
  )
}
